// Kanban and calendar: the same records, as cards grouped by a choice, or placed on the days of a month.
import { useMemo, useState } from "react";
import type { Field, Links, Person, Row, Value, View, ViewConfig } from "./pApi";
import { CellValue } from "./Cell";
import { addDays, groupKey, groupLabel, groupOrder, text, todayIso, toneOf } from "./format";

type Common = {
  view: View; fields: Field[]; rows: Row[]; links: Links; canEdit: boolean;
  onEdit: (rowId: string, values: Record<string, unknown>) => void;
  onAdd: (values: Record<string, unknown>) => void;
  onOpen: (id: string) => void;
  onConfig: (c: ViewConfig) => void;
  fieldsOf: (id: string) => Field[] | undefined;
};

function Card({ r, fields, shown, links, onOpen, draggable, fieldsOf }: { r: Row; fields: Field[]; shown: Field[]; links: Links; onOpen: () => void; draggable: boolean; fieldsOf: Common["fieldsOf"] }) {
  const primary = fields.find((f) => f.primary) ?? fields[0];
  return (
    <button className="pv-card" draggable={draggable} onDragStart={(e) => { e.dataTransfer.setData("text/row", r.id); e.dataTransfer.effectAllowed = "move"; }} onClick={onOpen}>
      <span className="pv-card-t">{primary ? text(primary, r.values[primary.key], links) || <span className="muted">Untitled</span> : `#${r.number}`}</span>
      {shown.map((f) => {
        const v = r.values[f.key];
        if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return null;
        return <span key={f.key} className="pv-card-f"><span className="pv-card-l">{f.name}</span><span className="pv-card-v"><CellValue f={f} v={v as Value} links={links} allFields={fields} fieldsOf={fieldsOf} /></span></span>;
      })}
    </button>
  );
}

export function KanbanView(p: Common) {
  const cfg = p.view.config;
  const groupable = p.fields.filter((f) => ["select", "person", "checkbox"].includes(f.type));
  const g = p.fields.find((f) => f.key === cfg.group && groupable.includes(f));
  const [over, setOver] = useState<string | null>(null);
  const shown = (cfg.cardFields ? cfg.cardFields.map((k) => p.fields.find((f) => f.key === k)) : p.fields.filter((f) => !f.primary && f.key !== g?.key && !(cfg.hidden ?? []).includes(f.key)).slice(0, 4)).filter((f): f is Field => !!f);
  const cols = useMemo(() => {
    if (!g) return [];
    const byKey = new Map<string, Row[]>();
    for (const r of p.rows) { const k = groupKey(g, r.values[g.key], p.links); (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(r); }
    let keys: string[];
    if (g.type === "select") { const ch = (g.options.choices ?? []).map((c) => c.name); keys = [...ch, ...[...byKey.keys()].filter((k) => k && !ch.includes(k))]; if (byKey.has("")) keys.push(""); }
    else if (g.type === "checkbox") keys = ["No", "Yes"];
    else keys = groupOrder(g, [...byKey.keys()]);
    return keys.map((k) => ({ key: k, rows: byKey.get(k) ?? [] }));
  }, [g, p.rows, p.links]);
  if (!g) return (
    <div className="panel pad pv-setup">
      <h3>Group the board by</h3>
      {groupable.length ? <div className="btn-row">{groupable.map((f) => <button key={f.key} className="btn" disabled={!p.canEdit} onClick={() => p.onConfig({ group: f.key })}>{f.name}</button>)}</div>
        : <p className="muted">A board groups records into columns by a Choice, Person or Checkbox field. Add one first.</p>}
    </div>
  );
  const valueFor = (k: string): unknown => (g.type === "checkbox" ? k === "Yes" : g.type === "person" ? (k || null) : (k || null));
  const people = g.type === "person" ? new Map(p.rows.map((r) => [(r.values[g.key] as Person | null)?.id ?? "", (r.values[g.key] as Person | null)?.name ?? ""])) : null;
  return (
    <div className="pv-board">
      {cols.map((c) => {
        const tone = g.type === "select" && c.key ? toneOf(g, c.key) : null;
        return (
          <section key={c.key} className={`pv-col${over === c.key ? " over" : ""}`} aria-label={groupLabel(g, c.key, p.rows)}
            onDragOver={(e) => { if (p.canEdit && e.dataTransfer.types.includes("text/row")) { e.preventDefault(); setOver(c.key); } }}
            onDragLeave={() => setOver((o) => (o === c.key ? null : o))}
            onDrop={(e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData("text/row"); const r = p.rows.find((x) => x.id === id); if (r && groupKey(g, r.values[g.key], p.links) !== c.key) p.onEdit(id, { [g.key]: valueFor(c.key) }); }}>
            <div className="pv-col-h">
              {tone ? <span className="pv-pill" style={{ background: tone.bg, color: tone.fg }}>{c.key}</span> : <strong>{g.type === "person" ? (people?.get(c.key) || "Nobody") : groupLabel(g, c.key, p.rows)}</strong>}
              <span className="muted">{c.rows.length}</span>
            </div>
            <div className="pv-col-b">
              {c.rows.map((r) => <Card key={r.id} r={r} fields={p.fields} shown={shown} links={p.links} onOpen={() => p.onOpen(r.id)} draggable={p.canEdit} fieldsOf={p.fieldsOf} />)}
            </div>
            {p.canEdit && <button className="pv-col-add" onClick={() => p.onAdd({ [g.key]: valueFor(c.key) })}>+ Add</button>}
          </section>
        );
      })}
    </div>
  );
}

// ---------- calendar ----------
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function CalendarView(p: Common) {
  const cfg = p.view.config;
  const dateFields = p.fields.filter((f) => ["date", "created", "updated"].includes(f.type) || (f.type === "formula" && f.resultType === "date"));
  const df = p.fields.find((f) => f.key === cfg.date && dateFields.includes(f));
  const ef = p.fields.find((f) => f.key === cfg.end && f.type === "date");
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const [over, setOver] = useState<string | null>(null);
  const primary = p.fields.find((f) => f.primary) ?? p.fields[0];
  if (!df) return (
    <div className="panel pad pv-setup">
      <h3>Put records on the calendar by</h3>
      {dateFields.length ? <div className="btn-row">{dateFields.map((f) => <button key={f.key} className="btn" disabled={!p.canEdit} onClick={() => p.onConfig({ date: f.key })}>{f.name}</button>)}</div>
        : <p className="muted">A calendar places records on the day in a Date field. Add one first.</p>}
    </div>
  );
  const first = `${month}-01`;
  const dow = (new Date(first + "T00:00:00Z").getUTCDay() + 6) % 7;
  const start = addDays(first, -dow);
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const shownWeeks = days.slice(35).some((d) => d.slice(0, 7) === month) ? 6 : 5;
  const byDay = new Map<string, Row[]>();
  for (const r of p.rows) {
    const s = r.values[df.key]; if (typeof s !== "string") continue;
    const a = s.slice(0, 10), b = ef && typeof r.values[ef.key] === "string" ? (r.values[ef.key] as string) : a;
    for (let d = a, n = 0; d <= b && n < 62; d = addDays(d, 1), n++) (byDay.get(d) ?? byDay.set(d, []).get(d)!).push(r);
  }
  const today = todayIso();
  const step = (n: number) => { const [y, m] = month.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); setMonth(d.toISOString().slice(0, 7)); };
  const label = new Date(first + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const undated = p.rows.filter((r) => typeof r.values[df.key] !== "string").length;
  const editable = p.canEdit && df.type === "date";
  return (
    <div className="pv-cal">
      <div className="pv-cal-bar">
        <button className="btn" onClick={() => step(-1)} aria-label="Previous month">‹</button>
        <button className="btn" onClick={() => setMonth(today.slice(0, 7))}>Today</button>
        <button className="btn" onClick={() => step(1)} aria-label="Next month">›</button>
        <h3>{label}</h3>
        <span className="muted">by {df.name}{ef ? ` to ${ef.name}` : ""}{undated ? ` · ${undated} without a ${df.name.toLowerCase()}` : ""}</span>
      </div>
      <div className="pv-cal-grid" role="grid" aria-label={label}>
        {WEEK.map((w) => <div key={w} className="pv-cal-dow" role="columnheader">{w}</div>)}
        {days.slice(0, shownWeeks * 7).map((d) => {
          const items = byDay.get(d) ?? [];
          return (
            <div key={d} role="gridcell" className={`pv-cal-day${d.slice(0, 7) !== month ? " out" : ""}${d === today ? " today" : ""}${over === d ? " over" : ""}`}
              onDragOver={(e) => { if (editable && e.dataTransfer.types.includes("text/row")) { e.preventDefault(); setOver(d); } }}
              onDragLeave={() => setOver((o) => (o === d ? null : o))}
              onDrop={(e) => {
                e.preventDefault(); setOver(null);
                const id = e.dataTransfer.getData("text/row"); const r = p.rows.find((x) => x.id === id); if (!r) return;
                const from = String(r.values[df.key] ?? "").slice(0, 10); if (from === d) return;
                const vals: Record<string, unknown> = { [df.key]: d };
                if (ef && typeof r.values[ef.key] === "string" && from) vals[ef.key] = addDays(r.values[ef.key] as string, Math.round((Date.parse(d) - Date.parse(from)) / 864e5));
                p.onEdit(id, vals);
              }}
              onDoubleClick={() => editable && p.onAdd({ [df.key]: d })}>
              <div className="pv-cal-n">{Number(d.slice(8))}</div>
              {items.slice(0, 4).map((r) => (
                <button key={r.id} className="pv-cal-ev" draggable={editable} onDragStart={(e) => e.dataTransfer.setData("text/row", r.id)} onClick={() => p.onOpen(r.id)}>
                  {primary ? text(primary, r.values[primary.key], p.links) || "Untitled" : `#${r.number}`}
                </button>
              ))}
              {items.length > 4 && <span className="pv-cal-more">+{items.length - 4} more</span>}
            </div>
          );
        })}
      </div>
      {editable && <p className="muted" style={{ fontSize: 12 }}>Drag a record to another day to move it. Double-click a day to add a record on it.</p>}
    </div>
  );
}
